import { useMutation, useQuery } from '@tanstack/react-query'
import { admin } from '../../api'
import { setAlert, setIsLoading } from '../components/components-slice'
import { store } from '..'
import moment from 'moment'
import { queryClient } from '../..'
import { formatAmount } from '../../utils/functions'
import { setEmployee } from '../employee/reducer'

export function useGetAdmins() {
  return useQuery(
    ['admins'],
    () => {
      return admin.getAdmins()
    },
    {
      select: (data) => {
        let newData = []

        data.data.forEach((item) => {
          newData.push({
            value: item.id,
            label: `${item.first_name} ${item.last_name}`,
          })
        })

        return newData
      },
    }
  )
}

export function useGetRegistrationLink() {
  return useQuery(
    ['employee-registration-link'],
    () => {
      store.dispatch(setIsLoading(true))
      return admin.getEmployeeRegistrationLink()
    },
    {
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useRefreshInvitationLink() {
  return useMutation(
    () => {
      return admin.refreshInvitationLink()
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('employee-registration-link')
        store.dispatch(setAlert(true, 'success', 'Link refreshed successfully'))
      },
    }
  )
}

export function useChangeInvitationLinkStatus() {
  return useMutation(
    () => {
      return admin.changeInvitationLinkSatus()
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('employee-registration-link')
        store.dispatch(
          setAlert(true, 'success', 'Link status changed successfully')
        )
      },
    }
  )
}

export function useGetEmployeeGroups(limit, page, statusFilter, search) {
  return useQuery(
    ['employee-groups', { limit, page, statusFilter, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return admin.getEmployeeGroups({
        limit,
        page,
        statusFilter,
        search,
      })
    },
    {
      select: (data) => {
        data?.data?.forEach((group) => {
          group.computedActions = [0, 1, 2, 3]
          group.created = moment(group?.created).format('MMMM D, YYYY')
          if (group && group.description && group.description.length >= 30) {
            group.description = group.description.substring(0, 30) + '....'
          }
        })

        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useInviteSingleEmployees() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.inviteSingleEmployee(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        store.dispatch(setEmployee(response.data))
        queryClient.invalidateQueries('employees')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}
export function useCreateSingleEmployees() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.createSingleEmployee(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        store.dispatch(setEmployee(response.data))
        queryClient.invalidateQueries('employees')
        store.dispatch(
          setAlert(
            true,
            'success',
            'Employee added and activated successfully.'
          )
        )
      },
      onSettled: (response, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useInviteBulkEmployees() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.inviteBulkEmployee(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('employees')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useCreateBulkEmployees() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.createBulkEmployee(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('employees')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (response, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useDeleteSalary() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.deleteSalary(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('employee-salaries')
        queryClient.invalidateQueries('employee-salary')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGetEmployeeSalaries(limit, page, statusFilter, search) {
  // const formatter = new Intl.NumberFormat('en-NG');
  return useQuery(
    ['employee-salaries', { limit, page, statusFilter, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return admin.getEmployeeSalaries({
        limit,
        page,
        statusFilter,
        search,
      })
    },
    {
      select: (data) => {
        data?.data?.forEach((employee) => {
          employee.employeeName = `${employee?.first_name} ${employee.last_name}`
          employee.status = parseInt(employee.status)
          let additional_allowance = 0
          let additional_allowances = []
          if (employee?.additional_allowances) {
            additional_allowances = JSON.parse(employee.additional_allowances)
          }
          // let additional_allowances = JSON.parse(
          //   employee?.additional_allowances
          // )
          if (additional_allowances && additional_allowances?.length > 0) {
            additional_allowances?.forEach((item) => {
              additional_allowance += parseFloat(item.amount)
            })
          }
          employee.gross_pay =
            parseFloat(employee.basic_salary ? employee.basic_salary : 0) +
            parseFloat(
              employee.airtime_allowance ? employee.airtime_allowance : 0
            ) +
            parseFloat(employee.data_allowance ? employee.data_allowance : 0) +
            parseFloat(
              employee.housing_allowance ? employee.housing_allowance : 0
            ) +
            parseFloat(
              employee?.leave_allowance ? employee?.leave_allowance : 0
            ) +
            parseFloat(
              employee.lunch_allowance ? employee.lunch_allowance : 0
            ) +
            parseFloat(
              employee.transportation_allowance
                ? employee.transportation_allowance
                : 0
            ) +
            parseFloat(
              employee.utility_allowance ? employee.utility_allowance : 0
            ) +
            parseFloat(
              employee.other_allowance ? employee.other_allowance : 0
            ) +
            parseFloat(additional_allowance)

          employee.other_accum_allowance =
            parseFloat(
              employee.airtime_allowance ? employee.airtime_allowance : 0
            ) +
            parseFloat(employee.data_allowance ? employee.data_allowance : 0) +
            parseFloat(
              employee.leave_allowance ? employee.leave_allowance : 0
            ) +
            parseFloat(
              employee.lunch_allowance ? employee.lunch_allowance : 0
            ) +
            parseFloat(
              employee.utility_allowance ? employee.utility_allowance : 0
            ) +
            parseFloat(
              employee.other_allowance ? employee.other_allowance : 0
            ) +
            parseFloat(additional_allowance)

          employee.formatted_other_accum_allowance = formatAmount(
            parseFloat(employee.other_accum_allowance),
            2
          )
          employee.formatted_basic_salary = formatAmount(
            parseFloat(employee.basic_salary),
            2
          )
          employee.formatted_transportation_allowance = formatAmount(
            parseFloat(employee.transportation_allowance),
            2
          )
          employee.formatted_housing_allowance = formatAmount(
            parseFloat(employee.housing_allowance).toFixed(2),
            2
          )
          employee.formatted_gross_pay = formatAmount(
            parseFloat(employee.gross_pay),
            2
          )

          employee.formatted_employer_voluntary_pension = formatAmount(
            parseFloat(employee.employer_voluntary_pension),
            2
          )
          employee.formatted_employee_voluntary_pension = formatAmount(
            parseFloat(employee.employee_voluntary_pension),
            2
          )

          //compute action items
          employee.computedActions = [0, 1, 2]
        })

        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useAddSalary() {
  return useMutation(
    (payload) => {
      return admin.addSalary(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('employee-salaries')
        store.dispatch(setAlert(true, 'success', response.message))
      },
    }
  )
}

export function useGrossPayReverse() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.grossPayReverse(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (response, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useNetPayReverse() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.netPayReverse(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        store.dispatch(
          setAlert(true, 'success', 'Payroll breakdown calculated successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useExportSalaryTemplate() {
  return useMutation(
    () => {
      store.dispatch(setAlert(true, 'success', 'Fetching Export Data...'))
      return admin.getEmployeeForSalary()
    },
    {
      onSuccess: (data) => {
        store.dispatch(
          setAlert(
            true,
            'success',
            'Salary uploading template fetched successfully'
          )
        )
        return data
      },
      onSettled: (response, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useFullPayReverseBulk() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.fullPayReverseBulk(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (response, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useNetPayReverseBulk() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.netPayReverseBulk(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (response, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGrossPayReverseBulk() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.grossPayReverseBulk(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (response, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useAddBulkSalary() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.addBulkSalary(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('employee-salaries')
        queryClient.invalidateQueries('employee-salary')
        store.dispatch(setAlert(true, 'success', response.message))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGetEmployees(limit, page, statusFilter, search, country) {
  return useQuery(
    ['employees', { limit, page, statusFilter, search, country }],
    () => {
      store.dispatch(setIsLoading(true))
      return admin.getEmployees({
        limit,
        page,
        statusFilter,
        search,
        country,
      })
    },
    {
      select: (data) => {
        data?.data?.forEach((employee) => {
          employee.employeeName = `${employee.first_name} ${employee.last_name}`
          employee.date_joined = employee?.date_joined
            ? moment(employee?.date_joined).format('MMMM D, YYYY')
            : '-'
          employee.group_name = employee?.employee_group?.group_name || null

          if (employee.hr_status === 0) {
            //compute action items
            switch (employee.status) {
              case 0:
                employee.computedActions = [0, 3, 6, 9, 12]
                break
              case 1:
                employee.computedActions = [0, 4, 6, 7, 8, 9, 12]
                break
              case 2:
                employee.computedActions = [0, 1, 2, 5, 6, 7, 9, 12]
                break
              case 4:
                employee.computedActions = [0, 1, 2, 6, 7, 8, 9, 12]
                break
              case 5:
                employee.computedActions = [0, 6, 8, 10, 11, 12]
                break
              default:
                break
            }
          } else {
            //compute action items
            switch (employee.status) {
              case 0:
                employee.computedActions = [0, 3, 6, 9, 12]
                break
              case 1:
                employee.computedActions = [0, 4, 6, 8, 9, 12]
                break
              case 2:
                employee.computedActions = [0, 1, 2, 5, 6, 9, 12]
                break
              case 4:
                employee.computedActions = [0, 1, 2, 6, 8, 9, 12]
                break
              case 5:
                employee.computedActions = [0, 6, 8, 10, 11, 12]
                break
              default:
                break
            }
          }

          //add to payroll
          employee.computedActions.push(13)
          // employee.computedActions = [0, 1, 2, 3, 4,5, 6, 7];
        })

        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGetEmployee(id) {
  return useQuery(
    ['employee', { id }],
    () => {
      store.dispatch(setIsLoading(true))
      return admin.getEmployee(id)
    },
    {
      enabled: id !== null,
      onSuccess: (response, variables, context) => {
        store.dispatch(setEmployee(response.data))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGetOnboardingEmployeeGroups(
  limit,
  page,
  statusFilter,
  search
) {
  return useQuery(
    ['employee-onboarding-groups', { limit, page, statusFilter, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return admin.getEmployeeGroups({
        limit,
        page,
        statusFilter,
        search,
      })
    },
    {
      select: (data) => {
        let newData = []
        newData.push({ value: '', label: 'No Payroll Group Selected' })
        data?.data?.forEach((item) => {
          newData.push({ value: item.id, label: item.name })
        })
        return newData
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useAddEmployeesToGroup() {
  return useMutation(
    (payload) => {
      return admin.addEmployeesToGroup(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('employee-groups')
        store.dispatch(
          setAlert(
            true,
            'success',
            'Employee(s) added to payroll group successfully'
          )
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}


export function useEditEmployeeGroup() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.editEmployeeGroup(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('group-settings')
        store.dispatch(
          setAlert(true, 'success', 'Payroll group updated successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useAddEmployeeGroup() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return admin.addEmployeeGroup(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('employee-groups')
        store.dispatch(
          setAlert(true, 'success', 'Payroll group added successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGetSalarySettings() {
  return useQuery(
    ['company-settings'],
    () => {
      return admin.getSalarySettings()
    },
    {
      select(data) {
        data.total_rate = 100
        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGetEmployeeGroup(id) {
  return useQuery(
    ['employee-group', { id }],
    () => {
      return admin.getEmployeeGroup({
        id,
      })
    },
    {
      enabled: id !== null,
      select: (data) => {
        data.status = 1
        data?.employees?.data?.forEach((employee) => {
          employee.employeeName = `${employee.employee_first_name} ${employee.employee_last_name}`
        })

        return data
      },
      onSettled: () => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}